import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './ArticleDetail.css';

// Resolves the base public URL for static asset requests.
const getBaseUrl = () => process.env.PUBLIC_URL || '';

// Ensures cover images are loaded from the elaborated cover directory.
const normalizeCoverUrl = (coverUrl) => {
    if (!coverUrl) {
        return '';
    }
    return coverUrl.replace('/media/covers/', '/media/covers/elaborated/');
};

const COVER_VARIANT_WIDTHS = [320, 640, 960, 1280];
const COVER_DEFAULT_SIZES = '(max-width: 1024px) 100vw, 960px';

// Splits a string into normalized keyword tokens for overlap comparison.
const tokenizeKeywords = (value) => {
    if (!value) {
        return [];
    }
    return String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(' ')
        .map((token) => token.trim())
        .filter((token) => token.length > 2);
};

// Builds a keyword set from article metadata and text fields.
const buildArticleKeywordSet = (articleData, fallbackSlug) => {
    const tagTokens = (articleData?.tags || []).map((tag) => String(tag).toLowerCase());
    const categoryTokens = (articleData?.categories || []).map((category) => String(category).toLowerCase());
    const slugTokens = tokenizeKeywords(articleData?.slug || fallbackSlug);
    const videogameTokens = tokenizeKeywords(articleData?.videogame_name);
    const titleTokens = tokenizeKeywords(articleData?.title);
    return new Set([...tagTokens, ...categoryTokens, ...slugTokens, ...videogameTokens, ...titleTokens]);
};

// Normalizes a route value into a comparable article key without leading or trailing slashes.
const normalizeArticleKey = (value) => String(value || '').trim().replace(/^\/+|\/+$/g, '');

// Normalizes an article index path so path comparisons are stable.
const normalizeEntryPath = (value) => {
    const normalizedPath = String(value || '').trim();
    if (!normalizedPath) {
        return '';
    }
    return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
};

// Builds a detail route for an article index entry using id first, then legacy slug fallback.
const buildArticleDetailLink = (entry) => {
    const preferredId = normalizeArticleKey(entry?.id);
    if (preferredId) {
        return `/blog/${preferredId}`;
    }
    const fallbackSlug = normalizeArticleKey(entry?.slug || entry?.id);
    return `/blog/${fallbackSlug}`;
};

// Returns true when an index entry matches the current article key through id, slug, or path variants.
const isEntryMatchArticleKey = (entry, articleKey) => {
    const normalizedArticleKey = normalizeArticleKey(articleKey);
    if (!entry || !normalizedArticleKey) {
        return false;
    }

    const entryId = normalizeArticleKey(entry?.id);
    const entrySlug = normalizeArticleKey(entry?.slug);
    if (entryId === normalizedArticleKey || entrySlug === normalizedArticleKey) {
        return true;
    }

    const normalizedPath = normalizeEntryPath(entry?.path);
    if (!normalizedPath) {
        return false;
    }

    const expectedPaths = new Set([
        `/articles/${normalizedArticleKey}.json`,
        `/${normalizedArticleKey}.json`,
        `${normalizedArticleKey}.json`
    ]);

    if (expectedPaths.has(normalizedPath)) {
        return true;
    }

    return normalizedPath.endsWith(`/${normalizedArticleKey}.json`);
};

// Returns true when the index entry is the same article as the active article key.
const isSameArticle = (entry, articleKey) => {
    return isEntryMatchArticleKey(entry, articleKey);
};

// Finds the best index entry match for the current article key.
const findIndexEntryForArticleKey = (entries, articleKey) => {
    if (!Array.isArray(entries) || !articleKey) {
        return null;
    }
    return entries.find((entry) => isEntryMatchArticleKey(entry, articleKey)) || null;
};

// Scores the overlap between a source keyword set and an index entry keyword set.
const scoreKeywordOverlap = (sourceKeywords, entryKeywords) => {
    if (!sourceKeywords || !entryKeywords) {
        return 0;
    }
    let score = 0;
    entryKeywords.forEach((keyword) => {
        if (sourceKeywords.has(keyword)) {
            score += 1;
        }
    });
    return score;
};

// Selects related articles by keyword overlap and returns the top matches.
const selectRelatedArticles = (entries, articleData, articleKey, limit = 3) => {
    if (!Array.isArray(entries) || !entries.length) {
        return [];
    }
    const sourceKeywords = buildArticleKeywordSet(articleData, articleKey);
    return entries
        .filter((entry) => !isSameArticle(entry, articleKey))
        .map((entry) => {
            const entryKeywords = buildArticleKeywordSet(entry, entry?.slug || entry?.id);
            return {
                entry,
                score: scoreKeywordOverlap(sourceKeywords, entryKeywords)
            };
        })
        .sort((first, second) => {
            if (second.score !== first.score) {
                return second.score - first.score;
            }
            return (first.entry?.title || '').localeCompare(second.entry?.title || '');
        })
        .slice(0, limit)
        .map((item) => item.entry);
};

// Extracts the base path and file extension from a cover image URL.
const getCoverUrlParts = (coverUrl) => {
    if (!coverUrl) {
        return { basePath: '', extension: '' };
    }
    const normalizedUrl = normalizeCoverUrl(coverUrl);
    const cleanUrl = normalizedUrl.split('?')[0].split('#')[0];
    const lastSlashIndex = cleanUrl.lastIndexOf('/');
    const lastDotIndex = cleanUrl.lastIndexOf('.');
    if (lastDotIndex > lastSlashIndex) {
        return {
            basePath: cleanUrl.slice(0, lastDotIndex),
            extension: cleanUrl.slice(lastDotIndex).toLowerCase()
        };
    }
    return { basePath: cleanUrl, extension: '' };
};

// Selects the fallback extension to use for the original cover format.
const getCoverFallbackExtension = (coverUrl) => {
    const { extension } = getCoverUrlParts(coverUrl);
    return extension === '.png' ? '.png' : '.jpg';
};

// Builds a srcset string for a given cover URL and output extension.
const buildCoverVariantSrcSet = (coverUrl, outputExtension) => {
    const { basePath } = getCoverUrlParts(coverUrl);
    if (!basePath) {
        return '';
    }
    return COVER_VARIANT_WIDTHS
        .map((width) => `${basePath}-${width}w${outputExtension} ${width}w`)
        .join(', ');
};

// Builds the fallback src URL using the largest available generated width.
const buildCoverFallbackSrc = (coverUrl) => {
    const { basePath } = getCoverUrlParts(coverUrl);
    if (!basePath) {
        return '';
    }
    const fallbackExtension = getCoverFallbackExtension(coverUrl);
    const maxWidth = Math.max(...COVER_VARIANT_WIDTHS);
    return `${basePath}-${maxWidth}w${fallbackExtension}`;
};

// Formats a production year into a readable year string or returns an empty string when invalid.
const formatPublishedDate = (isoDate) => {
    if (!isoDate) {
        return '';
    }
    const trimmedValue = String(isoDate).trim();
    if (!trimmedValue || trimmedValue.toLowerCase() === 'n.d.') {
        return '';
    }
    const date = new Date(trimmedValue);
    if (!Number.isFinite(date.getTime())) {
        return '';
    }
    return date.getFullYear().toString();
};

const getPillLabel = (pillValue) => {
    if (pillValue === null || pillValue === undefined) {
        return '';
    }
    return String(pillValue).trim();
};

// Builds an absolute article JSON URL from a relative public path.
const buildArticleUrl = (articlePath) => `${getBaseUrl()}${normalizeEntryPath(articlePath)}`;

// Builds the legacy fallback article path when no index entry match is available.
const buildFallbackArticlePath = (articleKey) => `/articles/${normalizeArticleKey(articleKey)}.json`;

// Builds the index JSON URL for article metadata in the public folder.
const buildIndexUrl = () => `${getBaseUrl()}/articles/index.json`;

// Normalizes a rating value so it always returns a number between 0 and 5.
const normalizeRating = (value, fallback = 0) => {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) {
        return fallback;
    }
    return Math.min(5, Math.max(0, normalized));
};

// Selects the rating from the index payload by matching the active article key.
const findRatingForArticleKey = (entries, articleKey) => {
    if (!Array.isArray(entries) || !articleKey) {
        return null;
    }
    const match = findIndexEntryForArticleKey(entries, articleKey);
    return match ? normalizeRating(match.rating, null) : null;
};

// Loads the index payload safely and returns an array even on failure.
const fetchIndexEntries = async () => {
    try {
        const response = await fetch(buildIndexUrl());
        if (!response.ok) {
            return [];
        }
        const payload = await response.json();
        return Array.isArray(payload) ? payload : [];
    } catch (error) {
        return [];
    }
};

// Renders a five-star rating display with filled stars based on the provided value.
const renderRatingStars = (rating) => {
    const safeRating = normalizeRating(rating);
    return (
        <div className="retro-rating" aria-label={`Rated ${safeRating} out of 5`}>
            {Array.from({ length: 5 }, (_, index) => {
                const isFilled = index < safeRating;
                return (
                    <span
                        key={`star-${index}`}
                        className={`retro-rating__star${isFilled ? ' retro-rating__star--filled' : ''}`}
                        aria-hidden="true"
                    >
                        ★
                    </span>
                );
            })}
        </div>
    );
};

// Renders the full article detail view for the retro blog.
export function ArticleDetail() {
    const params = useParams();
    const slug = params?.slug;
    const wildcard = params?.['*'];
    const articleKey = normalizeArticleKey(wildcard || slug);
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [indexEntries, setIndexEntries] = useState([]);
    const [indexRating, setIndexRating] = useState(null);
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const coverImage = article?.images?.cover;

    // Scrolls to the top when navigating to a new article key.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [articleKey]);

    // Navigates back to the home page with the filter query prefilled.
    const handlePillSearchClick = (pillLabel) => {
        const normalizedLabel = String(pillLabel || '').trim();
        const encodedLabel = encodeURIComponent(normalizedLabel);
        const targetUrl = encodedLabel ? `/?filter=${encodedLabel}` : '/';
        navigate(targetUrl);
    };

    // Navigates back to the home page and scrolls the window to the top.
    const handleBackToHomeClick = (event) => {
        if (event?.preventDefault) {
            event.preventDefault();
        }
        navigate('/');
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    // Loads the full article payload by article key from the public folder.
    useEffect(() => {
        let isActive = true;

        // Fetches the article JSON payload and updates local state.
        const loadArticle = async () => {
            try {
                setStatus('loading');
                setErrorMessage('');

                const loadedIndexEntries = await fetchIndexEntries();
                const matchedEntry = findIndexEntryForArticleKey(loadedIndexEntries, articleKey);
                const resolvedArticlePath = matchedEntry?.path || buildFallbackArticlePath(articleKey);
                const response = await fetch(buildArticleUrl(resolvedArticlePath));
                if (!response.ok) {
                    throw new Error('Unable to load this article.');
                }
                const articlePayload = await response.json();
                const ratingFromIndex = findRatingForArticleKey(loadedIndexEntries, articleKey);
                if (isActive) {
                    setArticle(articlePayload);
                    setIndexEntries(loadedIndexEntries);
                    setIndexRating(ratingFromIndex);
                    setStatus('ready');
                }
            } catch (error) {
                if (isActive) {
                    setErrorMessage(error.message || 'Unable to load this article.');
                    setStatus('error');
                }
            }
        };

        if (articleKey) {
            loadArticle();
        } else {
            setStatus('error');
            setErrorMessage('Missing article slug.');
        }

        return () => {
            isActive = false;
        };
    }, [articleKey]);

    // Memoizes the related article selection for stable rendering.
    const relatedArticles = useMemo(
        () => selectRelatedArticles(indexEntries, article, articleKey, 3),
        [indexEntries, article, articleKey]
    );

    return (
        <div className="retro-article">
            <nav className="retro-article__nav">
                <div className="retro-article__brand">GIUGGIOLI.NET</div>
                <Link className="retro-article__back" to="/" onClick={handleBackToHomeClick}>
                    ← Back to home
                </Link>
            </nav>

            {status === 'loading' && (
                <div className="retro-article__state">Loading article...</div>
            )}

            {status === 'error' && (
                <div className="retro-article__state retro-article__state--error">
                    {errorMessage}
                </div>
            )}

            {status === 'ready' && article && (
                <article className="retro-article__content shared-page__content">
                    <header className="retro-article__header">
                        <p className="retro-article__kicker">Arcade archive</p>
                        <h1>{article.title}</h1>
                        <p className="retro-article__subtitle">{article.subtitle}</p>
                        {renderRatingStars(indexRating ?? article?.rating)}
                        <div className="retro-article__meta">
                            <span>{article.reading_time_minutes} min read</span>
                            {formatPublishedDate(article.production_year) && (
                                <span><b> [{formatPublishedDate(article.production_year)}]</b></span>
                            )}
                        </div>
                    </header>

                     {coverImage?.url && (
                         <div className="retro-article__hero">
                             <picture>
                                 <source
                                     type="image/avif"
                                     srcSet={buildCoverVariantSrcSet(coverImage.url, '.avif')}
                                     sizes={COVER_DEFAULT_SIZES}
                                 />
                                 <source
                                     type="image/webp"
                                     srcSet={buildCoverVariantSrcSet(coverImage.url, '.webp')}
                                     sizes={COVER_DEFAULT_SIZES}
                                 />
                                 <img
                                     src={buildCoverFallbackSrc(coverImage.url)}
                                     srcSet={buildCoverVariantSrcSet(
                                         coverImage.url,
                                         getCoverFallbackExtension(coverImage.url)
                                     )}
                                     sizes={COVER_DEFAULT_SIZES}
                                     alt={coverImage.alt || article.title}
                                 />
                             </picture>
                         </div>
                     )}
<div
                        className="retro-article__body"
                        dangerouslySetInnerHTML={{ __html: article?.content?.body || '' }}
                    />

                     {coverImage?.url && (
                         <div className="retro-article__cover-inline">
                             <picture>
                                 <source
                                     type="image/avif"
                                     srcSet={buildCoverVariantSrcSet(coverImage.url, '.avif')}
                                     sizes={COVER_DEFAULT_SIZES}
                                 />
                                 <source
                                     type="image/webp"
                                     srcSet={buildCoverVariantSrcSet(coverImage.url, '.webp')}
                                     sizes={COVER_DEFAULT_SIZES}
                                 />
                                 <img
                                     src={buildCoverFallbackSrc(coverImage.url)}
                                     srcSet={buildCoverVariantSrcSet(
                                         coverImage.url,
                                         getCoverFallbackExtension(coverImage.url)
                                     )}
                                     sizes={COVER_DEFAULT_SIZES}
                                     alt={coverImage.alt || article.title}
                                 />
                             </picture>
                         </div>
                     )}
                    <div className="retro-article__sections">
                        {(article?.content?.sections || []).map((section) => (
                            <section className="retro-article__section" key={section.heading}>
                                <h2>{section.heading}</h2>
                                <p
                                    dangerouslySetInnerHTML={{ __html: section.text || '' }}
                                />
                            </section>
                        ))}
                    </div>

                    {relatedArticles.length > 0 && (
                        <section className="retro-article__related">
                            <div className="retro-article__related-header">
                                <h2>Related Articles</h2>
                                <p>More pixel memories with overlapping themes.</p>
                            </div>
                            <div className="retro-article__related-grid">
                                {relatedArticles.map((related) => {
                                    const relatedLink = buildArticleDetailLink(related);
                                    const relatedKey = normalizeArticleKey(related?.id || related?.slug || related?.path || related?.title);
                                    const relatedCover = related?.images?.cover;
                                    return (
                                        <Link
                                            key={relatedKey}
                                            className="retro-article__related-card"
                                            to={relatedLink}
                                        >
                                            {relatedCover?.url && (
                                                <div className="retro-article__related-media">
                                                    <picture>
                                                        <source
                                                            type="image/avif"
                                                            srcSet={buildCoverVariantSrcSet(relatedCover.url, '.avif')}
                                                            sizes={COVER_DEFAULT_SIZES}
                                                        />
                                                        <source
                                                            type="image/webp"
                                                            srcSet={buildCoverVariantSrcSet(relatedCover.url, '.webp')}
                                                            sizes={COVER_DEFAULT_SIZES}
                                                        />
                                                        <img
                                                            src={buildCoverFallbackSrc(relatedCover.url)}
                                                            srcSet={buildCoverVariantSrcSet(
                                                                relatedCover.url,
                                                                getCoverFallbackExtension(relatedCover.url)
                                                            )}
                                                            sizes={COVER_DEFAULT_SIZES}
                                                            alt={relatedCover.alt || related.title}
                                                        />
                                                    </picture>
                                                </div>
                                            )}
                                            <div className="retro-article__related-body">
                                                <h3>{related.title}</h3>
                                                <p>{related.subtitle || related.abstract}</p>
                                            </div>
                                            <span className="retro-article__related-link">Read article →</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <footer className="retro-article__footer">
                        <div className="retro-card__chips">
                            {([article.production_year, ...(article.tags || [])] || [])
                                .map((category) => getPillLabel(category))
                                .filter(Boolean)
                                .map((category) => (
                                    <button
                                        key={category}
                                        type="button"
                                        className="retro-card__chip"
                                        onClick={() => handlePillSearchClick(category)}
                                    >
                                        {category}
                                    </button>
                                ))}
                        </div>
                        <Link className="retro-article__back" to="/" onClick={handleBackToHomeClick}>
                    ← Back to home
                </Link>
                    </footer>
                </article>
            )}
        </div>
    );
}
